/* eslint-disable @typescript-eslint/no-misused-promises */
import { Dispatch } from 'redux';
import { LOGOUT, CLEAR_ALL_PW } from '@comeon/actions-public';
import reducerRegistry from '@comeon/reducer-registry';
import { requestApi } from '~/services/api';
import {
	AppThunk,
	CancelPwReturnT,
	PendingWithdrawal,
	PendingWithdrawalsState,
	PendingWithdrawalsActions,
	CancelPendingWithdrawAction,
	ClearPendingWithdrawalAction,
	ClearPendingWithdrawalStatusAction
} from '../pendingwithdrawals/components/types';

type SuccessCbT = (response: CancelPwReturnT | null) => void;

const REDUCER_NAME = 'pendingwithdrawals';

// Constants for action types
const USING_HYDRATE_PENDING_WITHDRAWALS =
	'@komigen/mobile-frontend/pendingwithdrawals/USING_HYDRATE_PENDING_WITHDRAWALS';
const HYDRATE_PENDING_WITHDRAWALS = '@komigen/mobile-frontend/pendingwithdrawals/HYDRATE_PENDING_WITHDRAWALS';
const REFRESH_PENDING_WITHDRAWALS = '@komigen/mobile-frontend/pendingwithdrawals/REFRESH_PENDING_WITHDRAWALS';
const CANCEL_PENDING_WITHDRAW = '@komigen/mobile-frontend/pendingwithdrawals/CANCEL_PENDING_WITHDRAW';
const CLEAR_PENDING_WITHDRAW = '@komigen/mobile-frontend/pendingwithdrawals/CLEAR_PENDING_WITHDRAW';
const CLEAR_NOT_CANCELABLE_PENDING_WITHDRAW_STATUS =
	'@komigen/mobile-frontend/pendingwithdrawals/CLEAR_NOT_CANCELABLE_PENDING_WITHDRAW_STATUS';

async function wrappedRequestApi<T>({
	endpoint,
	payload,
	method,
	urlencodedContentType,
	netellerHeaders
}: {
	endpoint: string;
	payload?: object;
	method?: string;
	urlencodedContentType?: boolean;
	netellerHeaders?: boolean;
}): Promise<T> {
	try {
		const response = (await requestApi(endpoint, payload, method, urlencodedContentType, netellerHeaders)) as T;

		return response;
	} catch (err) {
		// eslint-disable-next-line no-console
		console.log('Pending Withdrawal Duck...', err);
		return {} as T;
	}
}

function hydratePendingWithdrawals(): AppThunk {
	return async (
		dispatch: Dispatch<PendingWithdrawalsActions>,
		getState: () => { pendingwithdrawals: PendingWithdrawalsState }
	) => {
		const { hydrated } = getState().pendingwithdrawals;
		if (hydrated) {
			return dispatch({
				type: USING_HYDRATE_PENDING_WITHDRAWALS,
				payload: {}
			});
		}

		const items: PendingWithdrawal[] = await wrappedRequestApi<PendingWithdrawal[]>({
			endpoint: '/PaymentFormDataService/getPendingWithdrawals?useDateFormat=true&invalidateCache=true'
		});

		return dispatch({
			type: HYDRATE_PENDING_WITHDRAWALS,
			payload: {
				items,
				hydrated: true,
				pending: false
			}
		});
	};
}

function refreshPendingWithdrawal(): AppThunk {
	return dispatch => {
		dispatch({
			type: REFRESH_PENDING_WITHDRAWALS
		});
		dispatch(hydratePendingWithdrawals());
	};
}

export async function cancelPWNew(flow: string, cancelId?: number) {
	const cancelUrl = `/PaymentFormDataService/player/cancelWithdrawal`;

	const payload = {
		...(cancelId && { accHistIds: [cancelId] }),
		scenarioFlow: `Cancelled by Player from ${flow} screen`
	};
	try {
		const response = await wrappedRequestApi<CancelPwReturnT>({ endpoint: cancelUrl, payload, method: 'POST' });

		return response;
	} catch (e) {
		console.error(e, 'PW cancel error'); // eslint-disable-line
		return null;
	}
}

function cancelPendingWithdrawal(cancelId: number, successCb: SuccessCbT): AppThunk {
	return async (
		dispatch: Dispatch<CancelPendingWithdrawAction>,
		getState: () => { pendingwithdrawals: PendingWithdrawalsState }
	) => {
		const { items: itemsToPendingState } = getState().pendingwithdrawals;

		const markPending: PendingWithdrawal[] = itemsToPendingState.map(item => {
			if (item.id === cancelId) {
				return { ...item, pending: true };
			}
			return item;
		});

		dispatch({
			type: CANCEL_PENDING_WITHDRAW,
			payload: {
				items: markPending
			}
		});

		const response = await cancelPWNew('Pending Withdrawal', cancelId);
		const successful = !!(response?.success && response?.successfulCount > 0);
		successCb(response);

		const markCanceled: PendingWithdrawal[] = itemsToPendingState.map(item => {
			if (item.id === cancelId) {
				return {
					...item,
					pending: false,
					canceled: successful,
					error: successful ? undefined : 'Cancellation failed'
				};
			}
			return item;
		});

		return dispatch({
			type: CANCEL_PENDING_WITHDRAW,
			payload: {
				items: markCanceled
			}
		});
	};
}

function clearPendingWithdrawalStatus(cancelId: number): AppThunk {
	return (
		dispatch: Dispatch<ClearPendingWithdrawalStatusAction>,
		getState: () => { pendingwithdrawals: PendingWithdrawalsState }
	) => {
		const { items } = getState().pendingwithdrawals;

		const updatedItems = items.map(item => {
			if (item.id === cancelId) {
				const { error, ...rest } = item;
				return rest;
			}
			return item;
		});

		return dispatch({
			type: CLEAR_NOT_CANCELABLE_PENDING_WITHDRAW_STATUS,
			payload: {
				items: updatedItems
			}
		});
	};
}

function clearPendingWithdrawal(): AppThunk {
	return (dispatch: Dispatch<ClearPendingWithdrawalAction>) => {
		dispatch({
			type: CLEAR_PENDING_WITHDRAW,
			payload: {}
		});
	};
}

const initialState: PendingWithdrawalsState = {
	hydrated: false,
	items: []
};

function reducer(
	state: PendingWithdrawalsState = initialState,
	action: PendingWithdrawalsActions
): PendingWithdrawalsState {
	switch (action.type) {
		case HYDRATE_PENDING_WITHDRAWALS:
			return { ...state, ...action.payload };

		case CANCEL_PENDING_WITHDRAW:
		case CLEAR_NOT_CANCELABLE_PENDING_WITHDRAW_STATUS:
			return {
				...state,
				items: action.payload.items
			};

		case REFRESH_PENDING_WITHDRAWALS:
			return {
				...state,
				pending: true,
				hydrated: false
			};

		case CLEAR_PENDING_WITHDRAW:
			return {
				...state,
				hydrated: false,
				items: []
			};

		case LOGOUT:
		case CLEAR_ALL_PW:
			return initialState;

		default:
			return state;
	}
}

reducerRegistry.register(REDUCER_NAME, reducer);

export {
	hydratePendingWithdrawals,
	refreshPendingWithdrawal,
	cancelPendingWithdrawal,
	clearPendingWithdrawal,
	clearPendingWithdrawalStatus
};

export default reducer;
